import { Injectable } from '@angular/core';
import { IlocalStorageObj } from './types';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  public setWithExpiration(
    key: string,
    data: Date | boolean | string | number | object | [],
    daysTillExpire?: number,
  ) {
    if (!key?.length || !data) {
      // console.error('error understanding input data');
      return;
    }

    const storedData: IlocalStorageObj = {
      value: data,
      expireDate: this.setExpireDate(daysTillExpire),
    };

    localStorage.setItem(key, JSON.stringify(storedData));
  }

  public getWithExpiration(
    key: string,
  ): Date | boolean | string | number | object | [] | null {
    let storedDataAsObj: IlocalStorageObj;
    const storedData: string = localStorage.getItem(key);

    if (storedData) {
      storedDataAsObj = JSON.parse(storedData);

      if (this.isExpired(key, storedDataAsObj.expireDate)) {
        storedDataAsObj = null;
      }
    }

    return storedDataAsObj?.value;
  }

  public updateWithExpiration(
    key: string,
    data: Date | boolean | string | number | object | [],
  ) {
    if (!key?.length || !data) {
      return;
    }

    const previousStoredData: IlocalStorageObj = JSON.parse(
      localStorage.getItem(key),
    );

    const storedData: IlocalStorageObj = {
      value: data,
      expireDate: previousStoredData.expireDate,
    };

    localStorage.setItem(key, JSON.stringify(storedData));
  }

  public clearLocalStorage(
    options: { exemptKeys: string[] } = { exemptKeys: [] },
  ): void {
    const persistedData = new Map();

    for (const key of options?.exemptKeys ?? []) {
      const value = localStorage.getItem(key);
      if (value) {
        persistedData.set(key, localStorage.getItem(key));
      }
    }

    localStorage.clear();

    for (const [key, value] of persistedData) {
      localStorage.setItem(key, value);
    }
  }

  public removeItemLocalStorage(key: string): void {
    localStorage.removeItem(key);
  }

  // sessionstorage //

  public setSessionWithExpiration(
    key: string,
    data: Date | boolean | string | number | object | [],
    daysTillExpire?: number,
  ) {
    if (!key?.length) {
      // console.error('error understanding key');
      return;
    }

    const storedData: IlocalStorageObj = {
      value: data,
      expireDate: this.setExpireDate(daysTillExpire),
    };

    sessionStorage.setItem(key, JSON.stringify(storedData));
  }

  public getSessionWithExpiration(
    key: string,
  ): Date | boolean | string | number | object | [] | null {
    let storedDataAsObj: IlocalStorageObj;
    const storedData: string = sessionStorage.getItem(key);

    if (storedData) {
      storedDataAsObj = JSON.parse(storedData);

      if (this.isExpired(key, storedDataAsObj.expireDate)) {
        storedDataAsObj = null;
      }
    }

    return storedDataAsObj?.value;
  }

  public clearSessionStorage(): void {
    sessionStorage.clear();
  }

  public removeItemSessionStorage(key: string): void {
    sessionStorage.removeItem(key);
  }

  // private methods

  private setExpireDate(daysTillExpire: number): string {
    const today: Date = new Date(new Date().setHours(0, 0, 0, 0));

    // bad value? set to 90 days default
    if (!daysTillExpire || daysTillExpire < 0) {
      // console.error('bad expiration value passed, defaulted to 90 days');
      daysTillExpire = 90;
    }
    // if over 1 year default to 1 year
    if (daysTillExpire > 365) {
      // console.error('expiration value passed was over 1 year, expiration was lowered to 1 year');
      daysTillExpire = 365;
    }

    return new Date(
      today.setDate(today.getDate() + daysTillExpire),
    ).toISOString();
  }

  private isExpired(key: string, expireDate: string): boolean {
    if (!expireDate || new Date(expireDate) < new Date()) {
      // remove from storage
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
      return true;
    }

    return false;
  }
}
